import { NodeType, TableNodeProperties } from "@features/builder/graph/types"
import { XYPosition } from "@xyflow/system"
import {
  BaseGraphAttribute,
  BaseGraphMetaZipGroup,
  BaseGraphMetaZipNode,
  BaseGraphZipNode,
} from "@store/services/converter/types"
import * as Y from "yjs"
import { TypedDoc, TypedMap } from "yjs-types"
import { VariantType } from "notistack"

export interface SocketLeaveRoom {
  room_name: string
  sid: string
}

export interface SocketCursorMessage {
  sid?: string
  room_name: string
  cursor: CursorPayload
}

export interface SocketJoinCursorRoom extends SocketCursorMessage {
  members: SocketSubscriber[]
}

export type SocketNodeDto = SocketNode | SocketTableNode

export interface SocketNode {
  id: string
  position: XYPosition
  type: Exclude<
    NodeType,
    NodeType.Group | NodeType.Table | NodeType.ForecastTable | NodeType.ScenarioTable
  >
  style: NodeSize
  parent?: string
  //  @TODO (Bartek Laskowski) 2025-08-18: convert to mandatory after graph migration. In about 30 days!
  hidden?: boolean
  data: {
    meta: BaseGraphMetaZipNode
    node: BaseGraphZipNode
    comments: string
  }
}

export interface NodeSize {
  width: number
  height: number
}

export interface SocketTableNode {
  id: string
  position: XYPosition
  type: NodeType.Table | NodeType.ForecastTable | NodeType.ScenarioTable
  style: NodeSize
  parent?: string
  //  @TODO (Bartek Laskowski) 2025-08-18: convert to mandatory after graph migration. In about 30 days!
  hidden?: boolean
  data: {
    meta: BaseGraphMetaZipNode
    attribute: BaseGraphAttribute<TableNodeProperties>
    comments: string
  }
}

export interface SocketMatrixNode {
  id: string
  position: XYPosition
  type: NodeType.ForecastTable | NodeType.ScenarioTable
  style: NodeSize
  parent?: string
  hidden?: boolean
  data: {
    meta: BaseGraphMetaZipNode
    attribute: BaseGraphAttribute<TableNodeProperties>
    comments: string
  }
}

export interface SocketGroupDto {
  id: string
  position: XYPosition
  style: NodeSize
  type: NodeType
  parent?: string
  hidden: boolean
  data: {
    meta: BaseGraphMetaZipGroup
    comments: string
  }
}

export type SocketEdgeDto = {
  id: string // "source-target" e.g. "n123-n456"
  source: SocketNodeDto["id"]
  target: SocketNodeDto["id"]
}

export type EditorGraphDto = {
  id: string
  nodes: string[] // SocketNodeDto[]
  groups: string[] // SocketGroupDto[]
  data: string // GraphDetails
}

export type SimulationFrequency = "yearly" | "quarterly" | "monthly" | ""
export type GraphTagRule = "Never" | "Always" | "More than one"

export type GraphTagDto = {
  Name: string
  Value: string
  Rule: GraphTagRule
  Description?: string
}

export type GraphDetails = {
  Version: string
  /** @deprecated this field is no longer used, use Version (Pascal case) instead */
  version: null
  description: string
  fileName: string | null
  Product: string
  Graph: string
  StartDate: string | null
  EndDate: string | null
  YearlyRollingStartMonth: string
  RollingHorizon: string
  SimulationFrequency: SimulationFrequency
  ScenarioSource: string
  ScenarioVersion: string | null
  Scenarios: string[] | null
  ScenarioDescription?: Record<string, { description: string }> // keys are scenario names
  DashboardS3Location: string | null
  DashboardS3Hash: string | null
  Calibrated: "Yes" | "No"
  ValidationDocument: string | null
  GraphTags?: GraphTagDto[]
  Overview?: string
}

export interface Subscriber {
  email: string
  first_name: string
  last_name: string
  name: string
}

export interface SocketSubscriber extends Subscriber {
  sid: string
}

export interface SocketLocNodeMessage {
  type: "error" | "acquire" | "release"
  graph_id: string
  node_id: string
  sid: string
  email: string
}

interface SocketGraphLock {
  graph_id: string
  sid: string
  email: string
}

export type SocketGraphLockMessage =
  | AcquireSocketLockGraphMessage
  | ReleaseSocketLockGraphMessage
  | ErrorSocketLockGraphMessage

export interface BaseSocketLockGraphkMessage {
  type: "error" | "acquire" | "release"
  graph_id: string
  sid: string
  email: string
  dt: number
}

export interface AcquireSocketLockGraphMessage extends BaseSocketLockGraphkMessage {
  type: "acquire"
  lock: SocketGraphLock
  message: null
}

export interface ReleaseSocketLockGraphMessage extends BaseSocketLockGraphkMessage {
  type: "release"
  lock: null
  message: null
}

export interface ErrorSocketLockGraphMessage extends BaseSocketLockGraphkMessage {
  type: "error"
  lock: SocketGraphLock
  message: string
}

export interface SocketLockSuccessMessage extends SocketLocNodeMessage {
  dt: number
  related_node_ids: string[]
}

export type SocketLlmDescriptionUpdateDto =
  | {
      task_id: string | null
      status: "completed"
      graph_id: string
      node_name: string
      request_user: string
      result: string
      error: null
    }
  | {
      task_id: string | null
      status: "failed"
      graph_id: string
      node_name: string
      request_user: string
      result: null
      error: string
    }

export type SocketLlmDescriptionUpdateMessage =
  | SocketLlmDescriptionUpdateDto
  | { data: SocketLlmDescriptionUpdateDto }

export interface SocketEditRoomMessage {
  room_name: string
  locks: SocketLocNodeMessage[]
  graph_lock: SocketGraphLock | null
  sid: string
}

export interface SocketJoinEditRoom extends SocketEditRoomMessage {
  members: SocketSubscriber[]
}

export type CursorPayload = CursorPosition & {
  id: string
  sid?: string
}

type CursorPosition = {
  x: number
  y: number
  // json of DOMRect
  // TODO kk: remove from payload, we're not using it, but if not present, then BE doesn't emit "join" message
  rect: {
    bottom: number
    height: number
    left: number
    right: number
    top: number
    width: number
    x: number
    y: number
  }
}

export type ServerToClientEvent = {
  res_graph_new_version: (response: GraphNewVersionDto) => void

  // cursor room events
  cursor_join: (response: SocketJoinCursorRoom) => void
  res_cursor: (response: SocketJoinCursorRoom) => void

  // edit graph room event
  crdt_join: (response: SocketJoinEditRoom) => void
  crdt_graph_get: (response: ArrayBuffer) => void
  crdt_update: (response: ArrayBuffer) => void

  // room leave event
  leave: (response: SocketLeaveRoom) => void

  // lock nodes
  lock: (response: SocketLocNodeMessage) => void

  llm_description_update: (response: SocketLlmDescriptionUpdateMessage) => void

  error: (response: { status: number; message: string }) => void
  notification: (response: NotificationDto) => void

  // lock whole graph
  graph_lock: (response: SocketGraphLockMessage) => void
}

export type ClientToServerEvent = {
  req_publish_internally: (payload: { id: string }) => void

  // cursor room events
  req_cursor_join: (payload: CursorPayload, ack: () => void) => void
  req_cursor_leave: (payload: { id: string }, ack: () => void) => void
  req_cursor: (payload: CursorPayload) => void // cursor change

  // edit graph room event
  crdt_join: (payload: { id: string }, ack: () => void) => void
  crdt_leave: (payload: { id: string }, ack: () => void) => void
  crdt_graph_get: (payload: { id: string }) => void
  crdt_update: (id: string, update: Uint8Array) => void

  // lock nodes
  lock_acquire: (payload: { id: string; node_id: string; related_node_ids: string[] }) => void
  lock_release: (payload: { id: string; node_id: string; related_node_ids: string[] }) => void

  // lock whole graph
  graph_lock_acquire: (payload: { id: string }) => void
  graph_lock_release: (payload: { id: string }) => void
}

export enum SocketRoomPrefix {
  CURSOR = "cursor",
  CRDT = "crdt",
}

// yjs reference to BaseGraphMetaZipNode
export type YBaseGraphMetaZipNode = TypedMap<{
  label: Y.Text
  description: Y.Text
  unit: Y.Text
  groups?: Y.Array<Y.Text>
  color: Y.Text
  "Calibration Mnem": Y.Text
  "Forecast Mnem": Y.Text
  "Scenario Mnem": Y.Text
  "Unit Format"?: Y.Text
  "Historic Mnem"?: Y.Text
}>

// yjs reference to BaseGraphZipNode
export type YBaseGraphZipNode = TypedMap<{
  name: Y.Text
  type: Y.Text // GraphNodeType
  properties: TypedMap<{
    parents_lst?: Y.Array<Y.Text>
    attributes_lst?: Y.Array<Y.Text>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    a_sample_value?: any
    df_cpd: TypedMap<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
      [key: string]: any
      parents_lst?: Y.Array<Y.Text>
      expression?: Y.Array<Y.Text>
    }>
  }>
}>

// yjs reference to BaseGraphTableNode
export type YBaseGraphTableNode = TypedMap<{
  data: Y.Array<Y.Array<number>>
  columns: Y.Array<Y.Text>
  index: Y.Array<Y.Text>
}>

// yjs reference to BaseGraphAttribute
export type YBaseGraphAttribute<T = YBaseGraphTableNode> = TypedMap<{
  name: Y.Text
  type: Y.Text // GraphNodeType
  properties: YBaseGraphTableNode
}>

// yjs reference to SocketNodeDto
export type YSocketNode = TypedMap<{
  id: Y.Text
  position: { x: number; y: number }
  type: Y.Text // NodeType
  parent?: Y.Text
  data: TypedMap<{
    label: Y.Text
    meta: YBaseGraphMetaZipNode
    node?: YBaseGraphZipNode
    attribute?: YBaseGraphAttribute
  }>
}>

// yjs referenfce to BaseGraphMetaZipGroup
export type YBaseGraphMetaZipGroup = TypedMap<{
  nodes: Y.Array<Y.Text>
  label: Y.Text
  color: Y.Text
  isCollapsed: boolean
  parent?: Y.Text
}>

// yjs reference to SocketGroupDto
export type YSocketGroup = TypedMap<{
  id: Y.Text
  position: { x: number; y: number }
  type: Y.Text // NodeType
  parent?: Y.Text
  data: TypedMap<{
    label: Y.Text
    meta: YBaseGraphMetaZipGroup
  }>
}>

// yjs reference to GraphDetails
export type YGraphDetails = TypedMap<{
  version: Y.Text
  description: Y.Text
  fileName: Y.Text | null
  Product: Y.Text
  Graph: Y.Text
  Version: Y.Text
  StartDate: Y.Text | null
  EndDate: Y.Text | null
  YearlyRollingStartMonth: Y.Text
  RollingHorizon: Y.Text
  SimulationFrequency: Y.Text
  ScenarioVersion: Y.Text | null
  Calibrated: Y.Text
}>

export type GraphDocumentMap = TypedMap<{
  // document's maps
  data: Y.Text
  nodes: TypedMap<{ [key: string]: Y.Text }>
  groups: TypedMap<{ [key: string]: Y.Text }>
  id: Y.Text
}>

export type GraphDocument = TypedDoc<
  {
    graph: GraphDocumentMap
  },
  {
    // document's arrays
  },
  {
    // document's texts
  }
>

export interface NotificationDto {
  message: string
  variant: VariantType
}

type LinkedGraphPreviousRunGroup = {
  run_group_id: string
  /** if no scenario is provided, default_scenario will be set to "__BASELINE" */
  default_scenario?: string
}

export type LinkedGraphConfig = {
  linked_graph_run_mode?: "MERGE" | "PARALLEL" | "PREVIOUS"
  /** use linked_graph_id as a key */
  linked_graph_previous_run_groups?: Record<string, LinkedGraphPreviousRunGroup>
}

/** http://dev.pgm-np.spglobal.com/docs#/default/editor_sample_api_v1_editor_sample_post */
export interface SampleRequestDto {
  id: string
  run_name: string
  num_samples: number
  seed: number
  contract_key?: string
  description?: string
  scenarios: string[]
  end_date: string
  validate: boolean
  sampling_engine: "EVIEWS"
  sanity_graph_hash: string
  /**
This key is documented on swagger as a string, but it's actually an object. 
Comment from pgm-backend: server/lib/multistep_graph/graph_sample.py 
For linked graph, there are three modes available: 
1. MERGE: Merge all parent graphs into the child graph.
2. PARALLEL: Run all parent graphs in parallel, and patch the result to the child graph once the parent graphs finish sampling.
3. PREVIOUS: Link the previous runs from the parent graphs to the child graph. You need to provide the run group ID of the previous runs for each linked graph, and optionally a default scenario to use if the scenario in the child graph does not exist in the previous run group.

linked_graph_config has two fields:
- linked_graph_run_mode: To specify which mode to use. Default to MERGE
- linked_graph_previous_run_groups: A dictionary where the key is the linked graph ID, and the value is an object containing the run_group_id and optional default_scenario. Required for PREVIOUS mode only

for example:
``` 
{
    "linked_graph_run_mode": "PREVIOUS",
    "linked_graph_previous_run_groups": {
        "linked_graph_id_1": {
            "run_group_id": "123456",
            "default_scenario": "Baseline"
        },
        "linked_graph_id_2": {
            "run_group_id": "789012"
            "default_scenario": "Baseline"
        }
    }
}
*/

  // linked_graph_config?: LinkedGraphConfig
  linked_graph_config?: string // LoL
}

export interface SampleResponseDto {
  run_group_name: string
  graph_id: string
  run_group_id: string
}

export interface GraphNewVersionDto {
  name: string
  version: string
  graph_id: string
  old_graph_id: string
}

export interface GraphNewDto {
  name: string
  description?: string
  product_key: string
  start_date: string | null
  end_date: string | null
  rolling_duration?: string
  simulation_frequency?: string
  yearly_rolling_start_month?: string
  scenario_source?: string
  scenario_version?: string
  owner_group?: string
  graph_tags: GraphTagDto[]
  overview: string
  scenario_descriptions?: Record<string, { description: string }>
}
